import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Text,
  useDisclosure,
  VStack,
  Divider,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import React, { useMemo, useRef } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Loan, CreditAgreement } from '@finley-technologies/finley-types';

import {
  LoansFiltersDrawerState,
  useLoansFilters,
} from '../hooks/useLoansFilters';
import { useGetPartiesByTypeAndCreditFacilityIdsQuery } from 'packages/organization/requests/organization.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { colors } from '~/packages/common-frontend/colors';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import { Icon } from '~/packages/common-frontend/components/icons';
import { IsolatedMultiSelect } from '~/packages/common-frontend/components/MultiSelect';

// styles
const dropdownButtonProps = {
  as: Button,
  minWidth: '10rem',
  variant: 'secondary-muted',
  fontWeight: 400,
  width: '100%',
};
const menuListProps = {
  bgColor: colors.background[100],
  py: '0',
};
const inputProps = {
  backgroundColor: 'white',
};
const menuItemOptionProps = {
  bgColor: colors.background[100],
  _hover: { bg: colors.background[500] },
};

interface LoansFilterDrawerProps {
  onFilterChange: (nextFilters: LoansFiltersDrawerState) => void;
  initialFilters: LoansFiltersDrawerState;
}

export const LoansFiltersDrawer = ({
  onFilterChange,
  initialFilters,
}: LoansFilterDrawerProps) => {
  const drawerControls = useDisclosure();
  const { facilities } = useCreditFacilitiesOrThrow();

  // Filters
  const { filters, setFilters } = useLoansFilters(initialFilters);

  const { data: lendersData } = useGetPartiesByTypeAndCreditFacilityIdsQuery({
    variables: {
      creditFacilityIds: filters.creditFacilityIds,
      partyType: CreditAgreement.DomainEntity.Items.PartyType.CapitalProvider,
    },
  });
  const lenderIdsToName = useMemo(() => {
    return (
      lendersData?.partiesByTypeAndCreditFacilityIds.map((party) => ({
        id: party.id,
        name: party.name,
      })) ?? []
    );
  }, [lendersData]);

  const filtersBtnRef = useRef(null);
  const isFiltersDrawerClosable =
    filters.creditFacilityIds.length > 0 &&
    filters.capitalProviderIds.length > 0 &&
    filters.calculatedLoanStatuses.length > 0;

  return (
    <>
      <Button
        variant="secondary"
        colorScheme="v2-aurora"
        ref={filtersBtnRef}
        onClick={drawerControls.onOpen}
        gap="0.5rem"
      >
        <Icon icon="filter" />
        Filters
      </Button>
      <Drawer
        placement="right"
        finalFocusRef={filtersBtnRef}
        size="xl"
        {...drawerControls}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton
            onClick={() => {
              setFilters(initialFilters);
              drawerControls.onClose();
            }}
          />
          <DrawerHeader textAlign="left" mt="1.5rem" px="3rem" pb="0">
            <Heading>Filters</Heading>
          </DrawerHeader>
          <Divider my="1.5rem" />
          <DrawerBody px="3rem">
            <VStack align="flex-start">
              <Heading fontSize="xl">Maturity Dates</Heading>
              <Text color="gray">Select a date range to apply the filter.</Text>
              <FullDateRangePicker
                onSelectDateRange={(newDateRange) => {
                  const [newStartDate, newEndDate] = newDateRange;
                  setFilters({
                    maturityDates: {
                      startDate: newStartDate
                        ? commonDate.dateToFullDateString(newStartDate)
                        : null,
                      endDate: newEndDate
                        ? commonDate.dateToFullDateString(newEndDate)
                        : null,
                    },
                  });
                }}
                currentDateRange={{
                  startDate: filters.maturityDates.startDate
                    ? commonDate.fullDateStrToDate(
                        filters.maturityDates.startDate,
                      )
                    : null,
                  endDate: filters.maturityDates.endDate
                    ? commonDate.fullDateStrToDate(
                        filters.maturityDates.endDate,
                      )
                    : null,
                }}
                w="100%"
              />
              <Divider my="1.5rem" />
              <Heading fontSize="xl">Origination Dates</Heading>
              <Text color="gray">Select a date range to apply the filter.</Text>
              <FullDateRangePicker
                onSelectDateRange={(newOriginationDateRange) => {
                  const [newStartDate, newEndDate] = newOriginationDateRange;
                  setFilters({
                    createdAtDates: {
                      startDate: newStartDate
                        ? commonDate.dateToFullDateString(newStartDate)
                        : null,
                      endDate: newEndDate
                        ? commonDate.dateToFullDateString(newEndDate)
                        : null,
                    },
                  });
                }}
                currentDateRange={{
                  startDate: filters.createdAtDates.startDate
                    ? commonDate.fullDateStrToDate(
                        filters.createdAtDates.startDate,
                      )
                    : null,
                  endDate: filters.createdAtDates.endDate
                    ? commonDate.fullDateStrToDate(
                        filters.createdAtDates.endDate,
                      )
                    : null,
                }}
                w="100%"
              />
              <Divider my="1.5rem" />
              <FormControl isInvalid={filters.creditFacilityIds.length === 0}>
                <FormLabel>
                  <Heading fontSize="xl">Credit facilities</Heading>
                  <Text color="gray">
                    Select the credit facilities to apply the filter.
                  </Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={dropdownButtonProps}
                  initialSelections={filters.creditFacilityIds}
                  inputProps={{ ...inputProps }}
                  isSearchable
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(creditFacilityIds) => {
                    setFilters({ creditFacilityIds });
                  }}
                  optionsHeader="Credit facilities"
                  options={facilities.map((facility) => ({
                    id: facility.id,
                    label: facility.name,
                    value: facility.id,
                  }))}
                  placeholderText="Select credit facilities"
                  showSelectedCount
                  titleText="All credit facilities"
                />
                <FormErrorMessage>
                  Select at least one facility.
                </FormErrorMessage>
              </FormControl>

              <Divider my="1.5rem" />
              <FormControl
                isInvalid={
                  filters.capitalProviderIds.filter((lenderId) =>
                    lenderIdsToName.find((lender) => lender.id === lenderId),
                  ).length === 0
                }
              >
                <FormLabel>
                  <Heading fontSize="xl">Lenders</Heading>
                  <Text color="gray">
                    Select the Lender(s) to apply the filter.
                  </Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={dropdownButtonProps}
                  inputProps={{ ...inputProps }}
                  isSearchable
                  initialSelections={filters.capitalProviderIds.filter(
                    (lenderId) =>
                      lenderIdsToName.find((lender) => lender.id === lenderId),
                  )}
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(capitalProviderIds) => {
                    setFilters({ capitalProviderIds });
                  }}
                  options={lenderIdsToName.map((lenderIdToName) => ({
                    id: lenderIdToName.id,
                    label: lenderIdToName.name,
                    value: lenderIdToName.id,
                  }))}
                  optionsHeader="Lenders"
                  placeholderText="Select lenders"
                  showSelectedCount
                  titleText="All lenders"
                />
                <FormErrorMessage>Select at least one lender.</FormErrorMessage>
              </FormControl>

              <Divider my="1.5rem" />
              <FormControl
                isInvalid={filters.calculatedLoanStatuses.length === 0}
              >
                <FormLabel>
                  <Heading fontSize="xl">Statuses</Heading>
                  <Text color="gray">
                    Select the status to apply the filter.
                  </Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={dropdownButtonProps}
                  initialSelections={filters.calculatedLoanStatuses}
                  inputProps={{ ...inputProps }}
                  isSearchable
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(calculatedLoanStatuses) => {
                    setFilters({ calculatedLoanStatuses });
                  }}
                  options={[...Loan.CalculatedLoanStatuses].map(
                    (loanStatus) => ({
                      id: loanStatus,
                      label: commonUtils.toTitleCase(loanStatus),
                      value: loanStatus,
                    }),
                  )}
                  optionsHeader="Statuses"
                  placeholderText="Select statuses"
                  showSelectedCount
                  titleText="All statuses"
                />
                <FormErrorMessage>
                  Select at least one loan status.
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </DrawerBody>
          <Divider />
          <DrawerFooter justifyContent="space-between" m="1.5rem">
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={() => {
                setFilters(initialFilters);
              }}
            >
              Reset Filters
            </Button>
            <Button
              isDisabled={!isFiltersDrawerClosable}
              variant="primary"
              colorScheme="v2-aurora"
              onClick={() => {
                onFilterChange(filters);
                drawerControls.onClose();
              }}
            >
              Apply Filters
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};
